<template>
    <div>
        <v-progress-linear indeterminate v-if="saving"></v-progress-linear>
        <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
        <v-form v-if="tenant" v-model="valid" ref="form" :disabled="saving || loading">
            <v-tabs dark>
                <v-tab>Attributes</v-tab>
                <v-tab>UI</v-tab>
                <v-tab>Configuration</v-tab>
                <v-tab>Dashboard</v-tab>
                <v-tab>Services</v-tab>
                <v-tab-item eager>
                    <v-row wrap dense>
                        <v-col cols="12" v-if="error">
                            <v-alert type="error">
                                {{ error }}
                            </v-alert>
                        </v-col>
                        <v-col cols="12" v-if="warning">
                            <v-alert type="warning">
                                {{ warning }}
                            </v-alert>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field label="Name" v-model="tenant.Tenant" :rules="[form_rules.required]"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field :disabled="tenantId != 'new' && tenant.AgentCodePrefix != null && tenant.AgentCodePrefix != ''" label="Agent Code Prefix" v-model="tenant.AgentCodePrefix"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="Contact Email" v-model="tenant.TenantEmail" :rules="[form_rules.required, form_rules.email]"></v-text-field>
                            <v-text-field label="CC Email" v-model="tenant.TenantConfig.emails.CCEmail" :rules="[form_rules.email]"></v-text-field>
                            <v-text-field label="Group Email" v-model="tenant.TenantConfig.emails.GroupEmail" :rules="[form_rules.email]"></v-text-field>
                            <v-checkbox label="Enable Email Sending" v-model="tenant.TenantConfig.features.EnableEmails"></v-checkbox>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="URL" v-model="tenant.TenantBaseURL" :rules="[form_rules.required]" messages="Please set up DNS for this domain"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="Opt CID" v-model="tenant.OptCID"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-select :disabled="saving" label="Parent Tenant" v-model="tenant.ParentTenantID" :items="tenant_list"></v-select>
                        </v-col>
                        <v-col cols="6">
                            <q-agent-lookup :filters="{'Status' : 'Active,Available'}" label="Top Agent for Tenant" v-model="tenant.TopLevelAgentCode" placeholder="Must be an existing Agent - speak to data team to set up."></q-agent-lookup>
                        </v-col>
                    </v-row>
                </v-tab-item>
                <v-tab-item eager>
                    <v-row wrap dense v-if="tenant.TenantConfig.services.SureLC">
                        <v-col cols="6">
                            <v-color-picker dot-size="31" mode="hexa" swatches-max-height="210" v-model="tenant.TenantConfig.features.UIColor"></v-color-picker>
                        </v-col>
                        <v-col cols="6">
                            <image-selector label="Logo" :key="'TenantLogo-' + tenant.ID" height="100px" width="300px" :show-image="true" v-model="tenant.TenantConfig.features.TenantLogo"></image-selector>
                            For best results, please upload a SVG file.
                            <v-checkbox label="White Header/Footer Text" v-model="tenant.TenantConfig.features.UIDarkMode"></v-checkbox>
                        </v-col>
                    </v-row>
                </v-tab-item>
                <v-tab-item eager>
                    <v-row wrap dense>
                        <v-col cols="12">
                            <strong>Scorecards</strong>
                            <v-col cols="4">
                                <v-checkbox label="Stats" v-model="tenant.TenantConfig.features.Stats"></v-checkbox>
                            </v-col>
                        </v-col>
                        <v-col cols="4" v-if="tenant.TenantConfig.features.Stats">
                            <v-checkbox label="Submitted Stats" v-model="tenant.TenantConfig.features.Scorecards.Submitted"></v-checkbox>
                        </v-col>
                        <v-col cols="4" v-if="tenant.TenantConfig.features.Stats">
                            <v-checkbox label="Placed Stats" v-model="tenant.TenantConfig.features.Scorecards.Placed"></v-checkbox>
                        </v-col>
                        <v-col cols="4" v-if="tenant.TenantConfig.features.Stats">
                            <v-checkbox label="Pending Stats" v-model="tenant.TenantConfig.features.Scorecards.Pending"></v-checkbox>
                        </v-col>
                        <v-col cols="4" v-if="tenant.TenantConfig.features.Stats">
                            <v-checkbox label="Closed Stats" v-model="tenant.TenantConfig.features.Scorecards.Closed"></v-checkbox>
                        </v-col>
                        <v-col cols="4" v-if="tenant.TenantConfig.features.Stats">
                            <v-checkbox label="Close Ratio Stats" v-model="tenant.TenantConfig.features.Scorecards.CloseRatio"></v-checkbox>
                        </v-col>
                        <v-col cols="4" v-if="tenant.TenantConfig.features.Stats">
                            <v-checkbox label="Unique Writer Stats" v-model="tenant.TenantConfig.features.Scorecards.UniqueWriters"></v-checkbox>
                        </v-col>
                    </v-row>
                    <v-row wrap dense>
                        <v-col cols="12">
                            <strong>Agent Config</strong>
                        </v-col>
                        <v-col cols="4">
                            <v-text-field label="Top Contract Level" v-model="tenant.TenantConfig.features.TopContractlevel"></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row wrap dense>
                        <v-col cols="12">
                            <strong>Options</strong>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Opt CRM" v-model="tenant.TenantConfig.features.OptCRM"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Lightspeed LMS" v-model="tenant.TenantConfig.features.LightspeedLMS"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Support + Zendesk" v-model="tenant.TenantConfig.features.Support"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Cross Sale Commissions" v-model="tenant.TenantConfig.features.CrossSaleCommissions"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Leaderboards" v-model="tenant.TenantConfig.features.Leaderboards"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="SureLC" v-model="tenant.TenantConfig.features.SureLC"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Applications" v-model="tenant.TenantConfig.features.Applications"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="DTC Widget" v-model="tenant.TenantConfig.features.DTCWidget"></v-checkbox>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Global Leads" v-model="tenant.TenantConfig.features.GlobalLeads"></v-checkbox>
                            Checking this will allow all agents to access all leads within this tenant. (CESC Agents)
                        </v-col>
                    </v-row>
                </v-tab-item>
                <v-tab-item eager>
                    <v-row wrap dense>
                        <v-col cols="12">
                            <strong>Dashboard</strong>
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Homepage" v-model="tenant.TenantConfig.features.Homepage"></v-checkbox>Requires creating a 'Homepage' page in the CMS.
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Contests" v-model="tenant.TenantConfig.features.Contests"></v-checkbox>Requires creating a 'Contests' category.
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="News" v-model="tenant.TenantConfig.features.News"></v-checkbox>Requires creating a 'News' category.
                        </v-col>
                        <v-col cols="4">
                            <v-checkbox label="Events" v-model="tenant.TenantConfig.features.Events"></v-checkbox>Requires creating an 'Events' category.
                        </v-col>
                    </v-row>
                </v-tab-item>
                <v-tab-item eager>
                    <v-row wrap dense v-if="tenant.TenantConfig.features.SureLC">
                        <v-col cols="12">
                            <strong>SureLC</strong>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="SureLC Username" v-model="tenant.TenantConfig.services.SureLC.username"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="SureLC Password" v-model="tenant.TenantConfig.services.SureLC.password"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="SureLC Base URL" v-model="tenant.TenantConfig.services.SureLC.base_url"></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row wrap dense v-if="tenant.TenantConfig.features.Support">
                        <v-col cols="12">
                            <strong>Zendesk</strong>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="Zendesk Sub Domain" v-model="tenant.TenantConfig.services.ZenDesk.subdomain"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="Zendesk Email" v-model="tenant.TenantConfig.services.ZenDesk.email"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="Zendesk Token" v-model="tenant.TenantConfig.services.ZenDesk.token"></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row wrap dense v-if="tenant.TenantConfig.features.Onboarding">
                        <v-col cols="12">
                            <strong>Onboarding Portal</strong>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="Onboarding Key" v-model="tenant.TenantConfig.services.OnboardingPortal.key"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="Onboarding Base URL" v-model="tenant.TenantConfig.services.OnboardingPortal.base_url"></v-text-field>
                        </v-col>
                    </v-row>
                </v-tab-item>
            </v-tabs>
            <v-btn block color="primary" @click="saveTenant">
                Save
                <v-progress-circular :disabled="saving" right size="30" v-if="saving" indeterminate></v-progress-circular>
            </v-btn>
        </v-form>
        <v-dialog v-model="showMediaLib" width="75%">
            <media-library v-on:usefile="useMediaFile"></media-library>
        </v-dialog>
    </div>
</template>
<script>
import QuilityAPI from '@/store/API/QuilityAPI.js'
import QAgentLookup from '@/components/utils/QAgentLookup.vue'
import ImageSelector from "@/CMS/editor/components/ImageSelector";
import { EventBus } from "@/event-bus";
import MediaLibrary from '@/CMS/editor/mediaLibrary.vue';
import Tenant from '@/store/Models/Tenant'
export default {
    props: ['tenantId'],
    data() {
        return {
            tenant: null,
            saving: false,
            warning: null,
            error: null,
            valid: false,
            loading: false,
            tenant_list: [],
            mediaCallback: function() {},
            documentCallback: function() {},
            showMediaLib: false,

        }
    },
    mounted: function() {
        this.reset()
        var that = this
        EventBus.$on('medialibrary', function(callback) {
            that.showMediaLib = true;
            that.mediaCallback = callback;
        });
    },
    computed: {

    },
    methods: {
        loadTenant: function(id) {
            this.loading = true
            var g = this
            QuilityAPI.getTenant(id, { "FullConfig": true }).then(function(json) {
                var data = g.setConfigDefaults(json.data);
                g.$set(g, "tenant", data);
                g.tenant_list = json.meta.tenant_list;
                g.loading = false;
            }).catch(function(err) {
                console.log(err)
                g.error = err.msg
                setTimeout(function() { g.error = null }, 6000)
                g.saving = false;
            });
        },
        saveTenant() {
            var g = this
            if (!this.$refs.form.validate()) {
                this.warning = "Please correct invalid fields."
                setTimeout(function() { g.warning = null }, 6000)
                this.saving = false;
                return false;
            }
            this.saving = true;
            //now process the promise.
            QuilityAPI.saveTenant(this.tenantId, this.tenant).then(function(resp) {
                    if (resp.success !== true) {
                        g.error = resp.msg
                        setTimeout(function() { g.error = null }, 6000)
                    } else {
                        Tenant.insert({ data: resp.data })
                        g.$emit('saved')
                    }
                    g.saving = false;
                })
                .catch(function(err) {
                    console.log(err)
                    g.error = err.msg
                    setTimeout(function() { g.error = null }, 6000)
                    g.saving = false;
                });
        },
        setConfigDefaults: function(data) {
            //define the default config struture here...
            var default_config = {
                emails: {},
                features: {
                    Scorecards: {},
                    EnableEmails : false
                },
                services: {
                    ZenDesk: {},
                    SureLC: {},
                    OnboardingPortal: {},
                    AgentSync: {}
                }
            }
            var combined_config = {
                ...default_config,
                ...data.TenantConfig
            }
            //handles old bad config formats...
            if (Array.isArray(combined_config.features.Scorecards)) {
                combined_config.features.Scorecards = {}
            }
            if (Array.isArray(combined_config.services.ZenDesk)) {
                combined_config.services.ZenDesk = {}
            }
            if (Array.isArray(combined_config.services.SureLC)) {
                combined_config.services.SureLC = {}
            }
            if (Array.isArray(combined_config.services.OnboardingPortal)) {
                combined_config.services.OnboardingPortal = {}
            }
            data.TenantConfig = combined_config
            return data;
        },
        reset() {
            this.tenant = null
            this.warning = null
            this.saving = false
            if (this.tenantId == 'new') {
                this.$set(this, "tenant_list", this.tenant_select);
                this.$set(this, "tenant", this.setConfigDefaults({}));
                return
            }
            this.loadTenant(this.tenantId)
        },
        useMediaFile(file) {
            this.mediaCallback(file);
            this.showMediaLib = false;
        },
    },
    watch: {
        'tenantId': function(newV) {
            this.$nextTick(this.reset)
        },
        'tenant.TenantConfig.features.Stats': function(newV) {
            if (newV) {
                return
            }
            if (this.tenant.TenantConfig == null) {
                return
            }
            if (this.tenant.TenantConfig.features == null) {
                return
            }
            if (this.tenant.TenantConfig.features.Scorecards == null) {
                return
            }
            if (!newV) {
                this.tenant.TenantConfig.features.Scorecards.Submitted = false
                this.tenant.TenantConfig.features.Scorecards.Placed = false
                this.tenant.TenantConfig.features.Scorecards.CloseRatio = false
                this.tenant.TenantConfig.features.Scorecards.UniqueWriters = false
            }
        },
    },
    components: {
        QAgentLookup,
        ImageSelector,
        MediaLibrary
    }
}
</script>
